import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Button,
  Chip,
  Modal,
  TextField,
  Radio,
  CircularProgress,
  Grid,
  Divider,
  Tooltip,
  Alert,
  Link,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import CircleOutlinedIcon from '@mui/icons-material/CircleOutlined';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import {
  COLOR_PRIMARY_1,
  COLOR_PRIMARY_2,
  COLOR_TEXT_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
} from 'src/utils/colors';
import {
  createListAPI,
  fetchAllLists,
  fetchDefaultListDetails,
  setAllSavedMarketListsLoadingAction,
  setSelectedListLoadingAction,
  updateListMarketsCountInAllSavedMarketListsAction,
  updateMarketInList,
} from 'src/store/Dashboard/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useCanAccessScalePlan } from 'src/hooks/useFeatureAccess';
import {
  COLOR_PRIMARY,
  LAST_SELECTED_MARKET_LIST_ID,
  SELECTED_MARKET_LIST_ORDER,
  USER_WISE_LIST_CREATE_LIMIT,
  USER_WISE_SAVE_LIST_LIMIT,
} from 'src/utils/const';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { User } from 'src/interfaces/iuser';
import CloseIcon from '@mui/icons-material/Close';

const formatList = (list: any) => {
  return {
    isDefault: list.is_default,
    id: list.list_id,
    name: list.list_name,
    createdAt: list.created_at,
    count: list.count,
  };
};

const formatListReverse = (list: any) => {
  return {
    is_default: list.isDefault,
    list_id: list.id,
    list_name: list.name,
    created_at: list.createdAt,
    count: list.count,
  };
};

const SavedMarketListsDropdown = ({
  mode,
  currentSelectedMarketObject,
  handleOnSampleClick,
  handleOutSideClose,
}: {
  mode?: 'saveListinExplorerPage';
  currentSelectedMarketObject?: {
    name: string;
    id: string;
    action: 'add' | 'remove';
    url: string;
  } | null;
  handleOnSampleClick: (plan: string) => void;
  handleOutSideClose?: () => void;
}) => {
  const user: User = useSelector((state: any) => state.dashboard.user);

  const currentSavedMarketsList = useSelector((state: any) => state.dashboard.savedMarkets);
  const allSavedMarketListsObject = useSelector(
    (state: any) => state.dashboard.allSavedMarketLists
  );
  const allSavedMarketLists = useMemo(
    () => [...allSavedMarketListsObject.data].map(list => formatList(list)),
    [allSavedMarketListsObject.data]
  );
  const allSavedMarketListsLoading = allSavedMarketListsObject.loading;

  const isScaleUser = useCanAccessScalePlan();
  const isUserAllowedToCreateList =
    !allSavedMarketListsLoading &&
    allSavedMarketLists.length &&
    allSavedMarketLists.length < USER_WISE_LIST_CREATE_LIMIT[user.subscriptionPlan];

  const isUserAllowedToAddToCurrentList =
    !allSavedMarketListsLoading &&
    currentSavedMarketsList &&
    Object.keys(currentSavedMarketsList?.data?.marketDetails || {}).length <
      USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan];

  const lastSelectedMarketListId = localStorage.getItem(LAST_SELECTED_MARKET_LIST_ID);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedList, setSelectedList] = useState<any | null>(null);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [error, setError] = useState('');

  const dispatch = useDispatch();

  const getSelectedListingCount = (listingID: string = selectedList?.id) => {
    if (allSavedMarketListsObject) {
      return allSavedMarketListsObject.data.find(list => list.list_id === listingID)?.count || 0;
    }
    return 0;
  };

  const getSelectedListOrder = () => {
    const selectedListOrderFromLocalstorage = localStorage.getItem(SELECTED_MARKET_LIST_ORDER);
    return selectedListOrderFromLocalstorage ? JSON.parse(selectedListOrderFromLocalstorage) : [];
  };

  const updateSelectedListOrder = (listID: string) => {
    const selectedListOrder = getSelectedListOrder();

    if (selectedListOrder.includes(listID)) {
      selectedListOrder.splice(selectedListOrder.indexOf(listID), 1);
    }

    selectedListOrder.unshift(listID);
    localStorage.setItem(SELECTED_MARKET_LIST_ORDER, JSON.stringify(selectedListOrder));
  };

  const isAllowedToAddToSelectedList =
    !allSavedMarketListsLoading &&
    getSelectedListingCount() < USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan];

  useEffect(() => {
    if (allSavedMarketLists.length > 0 && !selectedList) {
      setSelectedList(
        lastSelectedMarketListId
          ? allSavedMarketLists.find(list => list.id === lastSelectedMarketListId)
          : allSavedMarketLists.find(list => list.isDefault)
      );
    }
  }, [allSavedMarketLists]);

  const isFullList = (list: any) => {
    return getSelectedListingCount(list.id) >= USER_WISE_SAVE_LIST_LIMIT[user.subscriptionPlan];
  };

  const isAllListsFull = useMemo(() => {
    return allSavedMarketLists.every(list => isFullList(list));
  }, [allSavedMarketLists]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectList = (listID: string) => {
    updateSelectedListOrder(listID);

    setSelectedList(allSavedMarketLists.find(list => list.id === listID) || null);
    dispatch(setSelectedListLoadingAction(true));
    dispatch(
      fetchDefaultListDetails(
        [...allSavedMarketLists].map(list => formatListReverse(list)),
        0,
        null,
        listID
      )
    );

    // if (mode && mode === 'saveListinExplorerPage') {
    //   handleAddToList();
    // }

    handleClose();
  };

  const handleAddToList = () => {
    if (!isAllowedToAddToSelectedList) {
      !isScaleUser && handleOnSampleClick('scale');
      return;
    }

    !currentSavedMarketsList.loading &&
      selectedList &&
      currentSelectedMarketObject &&
      dispatch(
        updateMarketInList(
          currentSavedMarketsList.data,
          Number(currentSelectedMarketObject.id),
          currentSelectedMarketObject.action,
          currentSelectedMarketObject.name,
          false
        )
      );

    selectedList?.id &&
      dispatch(
        updateListMarketsCountInAllSavedMarketListsAction({ listId: selectedList?.id, count: 1 })
      );

    handleOutSideClose && handleOutSideClose();
  };

  const renderCreateListButton = (isAllListsFull: boolean | undefined = undefined) => (
    <Button
      fullWidth
      variant={'text'}
      startIcon={<AddCircleOutlineOutlinedIcon sx={{ color: '#667' }} />}
      sx={{
        borderRadius: 2,
        justifyContent: 'flex-start',
        color: COLOR_TEXT_1,
        fontSize: '1rem',
        fontWeight: 400,
      }}
      onClick={() => setIsCreateModalOpen(true)}
      disabled={isScaleUser && !isUserAllowedToCreateList}
    >
      {allSavedMarketListsLoading ? (
        <CircularProgress size={20} sx={{ color: COLOR_PRIMARY }} />
      ) : (
        'New Saved List'
      )}
    </Button>
  );

  const sortedLists = useMemo(() => {
    const selectedListOrder = getSelectedListOrder();

    if (!selectedListOrder.length) return allSavedMarketLists;

    return [...allSavedMarketLists].sort((a, b) => {
      const aIndex = selectedListOrder.indexOf(a.id);
      const bIndex = selectedListOrder.indexOf(b.id);

      const aInOrder = aIndex !== -1;
      const bInOrder = bIndex !== -1;

      if (aInOrder && bInOrder) {
        return aIndex - bIndex;
      }
      if (aInOrder) return -1;
      if (bInOrder) return 1;

      // Neither in selectedListOrder, sort by createdAt
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
  }, [allSavedMarketLists]);

  const renderMenuContents = () => {
    return (
      <>
        <Typography sx={{ px: 2, py: 1, fontWeight: 600, mb: 1 }}>Choose a List</Typography>
        <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
          {isAllListsFull && mode === 'saveListinExplorerPage' && (
            <MenuItem sx={{ py: '5px', background: '#fff' }}>
              {renderCreateListButton(isAllListsFull)}
            </MenuItem>
          )}
          {sortedLists.map((list, index) => (
            <MenuItem
              key={list.id + index}
              selected={selectedList?.id === list.id}
              onClick={() => handleSelectList(list.id)}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2 }}
              disabled={(mode === 'saveListinExplorerPage' && isFullList(list)) || undefined}
            >
              <Box display="flex" alignItems="center" sx={{ textTransform: 'capitalize' }}>
                <Radio
                  checked={selectedList?.name === list.name}
                  value={list.name}
                  size="small"
                  sx={{ mr: 0.5, p: 0.5 }}
                  inputProps={{ 'aria-label': list.name }}
                />
                <Typography variant="body1">
                  {list.isDefault ? 'Default List' : list.name}
                </Typography>
                {/* {isFullList(list) && <Chip label="Full" size="small" sx={{ ml: '4px' }} />} */}
                {list.isDefault && (
                  <Chip
                    label="Main"
                    size="small"
                    color="primary"
                    sx={{ ml: '10px', color: '#fff' }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
          {(!isAllListsFull || (isAllListsFull && mode !== 'saveListinExplorerPage')) && (
            <>
              <Divider orientation="horizontal" sx={{ my: 1 }} />
              <MenuItem sx={{ py: '5px', background: '#fff' }}>{renderCreateListButton()}</MenuItem>
            </>
          )}
        </Box>

        <Box sx={{ px: 2, pb: 1.5, pt: 0 }}>
          {mode && mode === 'saveListinExplorerPage' && (
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<BookmarkIcon />}
                  onClick={() => handleAddToList()}
                  sx={{ borderRadius: 2, color: '#fff', mt: '20px', fontSize: '14px' }}
                  disabled={currentSavedMarketsList.loading || !isAllowedToAddToSelectedList}
                >
                  {currentSavedMarketsList.loading || allSavedMarketListsLoading ? (
                    <CircularProgress size={20} sx={{ color: '#fff' }} />
                  ) : (
                    'Add to List'
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </Box>

        {isCreateModalOpen && (
          <Modal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)}>
            <Box
              sx={{
                background: '#fff',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 360,
                borderRadius: 3,
                boxShadow: 24,
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" fontWeight={700} mb={1} sx={{ position: 'relative' }}>
                Create a New List
              </Typography>
              <TextField
                label="List Name"
                value={newListName}
                onChange={e => setNewListName(e.target.value)}
                fullWidth
                autoFocus
                sx={{ mb: 2 }}
                error={!!error}
                helperText={error}
                disabled={!isScaleUser}
              />

              {!isScaleUser && (
                <Alert severity="warning">
                  You’ve reached the{' '}
                  <span style={{ textTransform: 'capitalize' }}>{user.subscriptionPlan}</span> plan
                  limit for multiple Saved Lists.{' '}
                  <Link
                    href={undefined}
                    onClick={() => handleOnSampleClick('scale')}
                    sx={{ color: COLOR_PRIMARY_2, cursor: 'pointer' }}
                  >
                    Upgrade to the Scale plan
                  </Link>{' '}
                  to create unlimited Saved Lists
                </Alert>
              )}

              <Box display="flex" gap={2} width="100%" mt={1}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ color: '#fff' }}
                  fullWidth
                  onClick={async () => {
                    if (!isScaleUser) {
                      handleOnSampleClick('scale');
                      handleClose();
                      handleOutSideClose && handleOutSideClose();
                      return;
                    }

                    if (!newListName.trim()) {
                      setError('List name is required');
                      return;
                    }
                    setIsCreateModalOpen(false);
                    dispatch(setAllSavedMarketListsLoadingAction(true));
                    await createListAPI(newListName);
                    dispatch(fetchAllLists());
                    setNewListName('');
                    setError('');
                  }}
                >
                  {!isScaleUser ? 'Upgrade' : 'Create List'}
                </Button>
              </Box>

              <IconButton
                onClick={() => {
                  setIsCreateModalOpen(false);
                  setNewListName('');
                  setError('');
                }}
                sx={{ position: 'absolute', top: 10, right: 10 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Modal>
        )}
      </>
    );
  };

  const CloseButton = (liftTop: number = 0) => (
    <IconButton
      sx={{ position: 'absolute', top: 10, right: 10, transform: `translateY(-${liftTop}px)` }}
      onClick={() => {
        handleOutSideClose && handleOutSideClose();
        setAnchorEl(null);
      }}
    >
      <CloseIcon />
    </IconButton>
  );

  if (!selectedList) return <></>;

  return mode === 'saveListinExplorerPage' ? (
    <Box sx={{ position: 'relative' }}>
      {CloseButton(-10)}
      {renderMenuContents()}
    </Box>
  ) : (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip title="Switch List">
        <Button
          onClick={handleClick}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: '26px !important' }} />}
          sx={{ color: COLOR_TEXT_2, textTransform: 'none', fontSize: '20px', fontWeight: 600 }}
        >
          {selectedList.isDefault ? 'Saved Markets' : selectedList.name}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { borderRadius: 2, width: 300, p: 1 } }}
      >
        {CloseButton(4)}
        {renderMenuContents()}
      </Menu>
    </Box>
  );
};

export default SavedMarketListsDropdown;
